"use client";

import { Card, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import React from "react";
import { useParams } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetCampaignLogs from "@/hooks/requests/campaign-logs/use-get-campaign-logs";
import useFilters from "@/hooks/use-filters";
import usePagination from "@/hooks/use-pagination";
import { ICampaignLog } from "@/interfaces/campaign-logs.interface";
import { CampaignLogsMessages } from "@/messages/campaigns/logs/campaign-logs.messages";

import FiltersForm from "./components/FiltersForm";
import { CampaignLogTable } from "./components/Table";
import { initialFilterValues } from "./constants";

type Props = {
  messages: CampaignLogsMessages;
};

function CampaignLogsPage({ messages }: Props) {
  const { campaignId, scheduleDate } = useParams<{ campaignId: string; scheduleDate: string }>();

  const { lang } = useLanguage();
  const pagination = usePagination();
  const { pageNumber, pageSize } = pagination;

  const filter = useFilters<ICampaignLog>({ initialValue: initialFilterValues });
  const filters = filter.filters;

  const { data, isLoading, error } = useGetCampaignLogs({
    campaignId: parseInt(campaignId, 10),
    scheduleDate,
    filters,
    pageNumber,
    pageSize,
  });

  const hasLogs = !!data && data.items.length > 0;
  const noLogs = !!data && data.items.length === 0;

  const shouldLoadLogTable = hasLogs && !isLoading && !error;
  const shouldLoadError = !isLoading && !!error;
  const shouldLoadNoLogsFound = noLogs || typeof data === "string";

  const BREAD_CRUMBS = [
    { title: messages.breadCrumbTitle, href: ROUTE_URLS.campaignStatistics(lang, campaignId) },
  ];

  return (
    <PageLayout messages={messages} breadCrumbs={BREAD_CRUMBS} title={messages.pageTitle}>
      <PageContainer>
        <Formik initialValues={filters} validateOnChange={false} onSubmit={filter.changeFilters}>
          <FiltersForm messages={messages} />
        </Formik>

        <Card sx={({ functions: { pxToRem } }) => ({ marginTop: pxToRem(16) })}>
          <Suspense isSuspended={isLoading} fallback={<CircularProgress size={50} />}>
            <MDBox p={2}>
              <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6">{messages.pageTitle}</MDTypography>
              </MDBox>

              {shouldLoadLogTable && (
                <CampaignLogTable pagination={pagination} data={data} messages={messages} />
              )}

              {shouldLoadError && (
                <AlertBlock
                  title={messages.errorToLoadLogs}
                  description={error.message}
                  alt={messages.noLogsFoundAlt}
                />
              )}

              {shouldLoadNoLogsFound && (
                <AlertBlock
                  title={messages.noLogsFoundError}
                  description={messages.noLogsFoundDescription}
                  alt={messages.noLogsFoundAlt}
                />
              )}
            </MDBox>
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}

export default CampaignLogsPage;
